/**
 * Le brouillon qu'un éditeur de document a ouvert (plan document-drafts) — les deux gestes qui
 * en sortent, communs aux éditeurs images et vidéo :
 *  - commit : Enregistrer / Valider / Publier — le document en base (autosave.settle), puis
 *    POST commit : l'origine reçoit le document, le brouillon disparaît ;
 *  - quit : Quitter — confirmation si le document a changé, puis DELETE du brouillon ;
 *  - download : Télécharger — le document en base, puis POST de l'export (le serveur exporte
 *    une copie figée du brouillon), dont la carte se pose sur le plateau des exports. On reste
 *    dans l'éditeur.
 *
 * Le serveur décide où aller : sa réponse JSON porte l'en-tête `Location` (la bibliothèque de
 * l'origine), comme les réponses JSON du scaffold Rails. La lib décide seulement COMMENT y
 * aller : dans un side panel, elle le ferme ; en pleine page, elle visite `Location` en
 * `replace` — le brouillon détruit ne reste jamais dans l'historique.
 *
 * Fermer le panel autrement (Échap, closeSidePanel) passe par `side-panel:before-close` : la
 * lib l'annule et en fait un Quitter. Quand c'est elle qui ferme, après un geste, elle laisse
 * passer (état interne `left`).
 *
 * LEGACY_EDITOR : l'éditeur peut vivre dans une modale (SidePanelHelper#editor_modal, un
 * dialog) — même contrat : Échap (`cancel`) devient un Quitter, et la sortie ferme le dialog
 * et vide son frame, pour qu'il se rouvre propre.
 *
 * Sans contrat (lecteur readonly, PROVISOIRE — cf. documents/_editor), Quitter ne jette rien
 * et ferme simplement le panel.
 */
export class DocumentDraft {
  /**
   * @param {HTMLElement} element la racine de l'éditeur
   * @param {string} commitUrl / discardUrl : le contrat posé par la vue (vides hors brouillon)
   * @param {Autosave} autosave l'autosave de l'éditeur, vidée avant le commit
   * @param {() => boolean} isDirty le document a-t-il changé depuis l'ouverture ?
   * @param {string} confirmMessage la confirmation de Quitter
   */
  constructor({ element, commitUrl, discardUrl, autosave, isDirty, confirmMessage }) {
    this.element = element;
    this.commitUrl = commitUrl;
    this.discardUrl = discardUrl;
    this.autosave = autosave;
    this.isDirty = isDirty;
    this.confirmMessage = confirmMessage;
    this.left = false;

    this.panel = element.closest("[data-controller~='side-panel']");
    this.dialog = element.closest("[data-controller~='dialog']")?.querySelector("dialog");
    this._onBeforeClose = (event) => {
      if (this.left) return;
      event.preventDefault();
      this.quit();
    };
    this.panel?.addEventListener("side-panel:before-close", this._onBeforeClose);
    this.dialog?.addEventListener("cancel", this._onBeforeClose);
  }

  destroy() {
    this.panel?.removeEventListener("side-panel:before-close", this._onBeforeClose);
    this.dialog?.removeEventListener("cancel", this._onBeforeClose);
  }

  /** Rend false en cas d'échec : l'éditeur réactive son bouton et montre l'erreur. */
  async commit() {
    if (!this.commitUrl || this.left) return false;

    await this.autosave?.settle();
    return this._request("POST", this.commitUrl);
  }

  /** Rend false si l'utilisateur renonce, ou en cas d'échec. */
  async quit() {
    if (this.left) return false;
    if (!this.discardUrl) return this._leave(null);
    if (this.isDirty() && !window.confirm(this.confirmMessage)) return false;

    return this._request("DELETE", this.discardUrl);
  }

  /**
   * @param {string} url la création d'un export du brouillon (compositions/:id/exports)
   * @param {string} failureMessage le toast d'un échec (réseau, serveur)
   */
  async download(url, failureMessage) {
    try {
      await this.autosave?.settle();
      const response = await fetch(url, {
        method: "POST",
        headers: { Accept: "text/vnd.turbo-stream.html",
                   "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
      });
      if (!response.ok) throw new Error(`export ${response.status}`);

      window.Turbo?.renderStreamMessage(await response.text());
      return true;
    } catch {
      window.showToast?.({ detail: { message: failureMessage } });
      return false;
    }
  }

  async _request(method, url) {
    const response = await fetch(url, {
      method,
      headers: { Accept: "application/json",
                 "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
    });
    if (!response.ok) return false;

    return this._leave(response.headers.get("Location"));
  }

  _leave(location) {
    this.left = true;
    if (this.panel) {
      window.dispatchEvent(new CustomEvent("close-side-panel", { detail: { id: this.panel.id } }));
    } else if (this.dialog) {
      this.dialog.close();
      const frame = this.element.closest("turbo-frame");
      if (frame) {
        frame.removeAttribute("src");
        frame.removeAttribute("complete");
        frame.innerHTML = "";
      }
    } else if (location) {
      window.Turbo?.visit(location, { action: "replace" });
    }
    return true;
  }
}
